/**
 * Role Master taxonomy option lists (Function / HR department / Team 1-3),
 * derived from GET /org/structure/tree — the taxonomy's source of truth (the
 * same data the Structure tab renders; server-cached 15 min).
 *
 * Team options are the union of all three team levels: the organogram's Team
 * filter matches a seat whose role carries the value at ANY level.
 *
 * Module-level cache so the Organogram view and the Export dialog share one
 * fetch per session.
 */
import { useEffect, useState } from 'react';
import {
  organizationService,
  type OrgStructureTreeNode,
} from '@/services/api/organizationService';

export interface RmTaxonomyOptions {
  functions: string[];
  hrDepartments: string[];
  teams: string[];
  /** Grades ordered by SENIORITY, most senior first (F1 … E5 … B1, then any non-standard). */
  grades: string[];
}

const EMPTY: RmTaxonomyOptions = { functions: [], hrDepartments: [], teams: [], grades: [] };

/**
 * Eduvos grades read letter-then-number with BOTH descending in seniority:
 * F1 (CEO) > E5 (CAO) > E2 > D4 > C3 > B4 … Sort accordingly so a "from F to B"
 * range means what it says; anything non-standard (e.g. "NG") sorts last.
 */
export function sortGradesBySeniority(grades: string[]): string[] {
  const key = (g: string): [number, number, number, string] => {
    const m = /^([A-Za-z])\s*(\d+)?$/.exec(g.trim());
    if (!m) return [1, 0, 0, g];
    return [0, -m[1].toUpperCase().charCodeAt(0), -(m[2] ? Number(m[2]) : 0), g];
  };
  return [...grades].sort((a, b) => {
    const ka = key(a); const kb = key(b);
    for (let i = 0; i < 3; i++) {
      if (ka[i] !== kb[i]) return (ka[i] as number) - (kb[i] as number);
    }
    return String(ka[3]).localeCompare(String(kb[3]));
  });
}

let cached: RmTaxonomyOptions | null = null;
let inflight: Promise<RmTaxonomyOptions> | null = null;

async function fetchOptions(): Promise<RmTaxonomyOptions> {
  const tree = await organizationService.getStructureTree();
  const functions = new Set<string>();
  const departments = new Set<string>();
  const teams = new Set<string>();
  const grades = new Set<string>();
  const walk = (n: OrgStructureTreeNode) => {
    if (n.type === 'role') {
      if (n.grade_level) grades.add(n.grade_level.trim());
      return;
    }
    if (n.type === 'function' && !n.name.startsWith('Unassigned')) functions.add(n.name);
    if (n.type === 'department' && n.name !== '(No department)') departments.add(n.name);
    if (n.type === 'team') teams.add(n.name);
    n.children.forEach(walk);
  };
  tree.functions.forEach(walk);
  const sort = (s: Set<string>) => Array.from(s).sort((a, b) => a.localeCompare(b));
  return {
    functions: sort(functions),
    hrDepartments: sort(departments),
    teams: sort(teams),
    grades: sortGradesBySeniority(Array.from(grades)),
  };
}

export function useRmTaxonomyOptions(): RmTaxonomyOptions {
  const [opts, setOpts] = useState<RmTaxonomyOptions>(cached ?? EMPTY);

  useEffect(() => {
    if (cached) return;
    let alive = true;
    inflight = inflight ?? fetchOptions();
    inflight
      .then((o) => {
        cached = o;
        if (alive) setOpts(o);
      })
      .catch((err) => {
        console.error('RM taxonomy options fetch failed', err);
        inflight = null; // allow a retry on next mount
      });
    return () => { alive = false; };
  }, []);

  return opts;
}
